import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { ThemeProvider } from '../theme/ThemeContext';
import { initStore } from './store';
import App from './App';
import { setupState, setUIMode, setMountedFromPlugin } from './recorderSlice';
import getMiddleware from './middleware';
import { EventEmitter, EventListener, EventMap } from './EventEmitter';
import { getSettings } from '../services/authService';
import { setToken } from '../lib/localStorageHandler';
import { setHeader } from '../lib/baseAxios';
import { cleanup } from './Recorder/utils/streamUtils';
import { cleanupTitleNotifications } from './utils/titleNotification';

const getRecorderComponent = (store: ReturnType<typeof initStore>) => {
  const Recorder = () => {
    return (
      <ThemeProvider>
        <Provider store={store}>
          <App />
        </Provider>
      </ThemeProvider>
    );
  };
  return Recorder;
};

export default class ScreenAppRecorder {
  apiKey: string;

  parentElementSelector: string;

  teamId: string;

  folderId: string;

  settings: Parameters<typeof setupState>[0]['settings'];

  uiMode: 'fullscreen' | 'popup';

  isPlugin: boolean;

  private eventEmitter: EventEmitter;

  private root: ReturnType<typeof createRoot> | null = null;

  constructor({
    apiKey,
    parentElementSelector,
    teamId,
    folderId,
    settings,
    uiMode = 'fullscreen',
    isPlugin = false,
  }: {
    apiKey: string;
    parentElementSelector?: string;
    teamId: string;
    folderId: string;
    settings?: Parameters<typeof setupState>[0]['settings'];
    uiMode?: 'fullscreen' | 'popup';
    isPlugin?: boolean;
  }) {
    this.apiKey = apiKey;
    this.parentElementSelector = parentElementSelector || '#screenapp-recorder';
    this.teamId = teamId;
    this.folderId = folderId;
    this.settings = settings;
    this.uiMode = uiMode;
    this.isPlugin = isPlugin;
    this.eventEmitter = new EventEmitter();
    this.eventEmitter.on('uploaded', () => {
      this.destroy();
    });
    this.eventEmitter.on('canceled', () => {
      this.destroy();
    });
    this.eventEmitter.on('error', (event) => {
      console.log('ScreenAppRecorder received error event:', event.detail);

      // Show persistent error message that waits for user interaction
      this.showErrorMessage(event.detail.message);

      // Delay cleanup to allow user to see the error
      setTimeout(() => {
        this.destroy();
      }, 100);
    });
  }

  showErrorMessage(message: string) {
    // Remove any existing error display
    const existingError = document.getElementById('recorder-error-display');
    if (existingError) {
      existingError.remove();
    }

    // Create error container
    const errorContainer = document.createElement('div');
    errorContainer.id = 'recorder-error-display';
    errorContainer.className =
      'fixed top-4 left-1/2 transform -translate-x-1/2 z-50 max-w-sm w-full mx-4';

    // Create main error card
    const errorCard = document.createElement('div');
    errorCard.className =
      'bg-red-50 border border-red-200 rounded-lg shadow-lg p-4';

    // Create content wrapper
    const contentWrapper = document.createElement('div');
    contentWrapper.className = 'flex items-start justify-between';

    // Create left side (icon + text)
    const leftSide = document.createElement('div');
    leftSide.className = 'flex items-start';

    // Create error icon
    const errorIcon = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'svg',
    );
    errorIcon.setAttribute(
      'class',
      'w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0',
    );
    errorIcon.setAttribute('fill', 'currentColor');
    errorIcon.setAttribute('viewBox', '0 0 20 20');
    const errorIconPath = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'path',
    );
    errorIconPath.setAttribute('fill-rule', 'evenodd');
    errorIconPath.setAttribute(
      'd',
      'M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z',
    );
    errorIconPath.setAttribute('clip-rule', 'evenodd');
    errorIcon.appendChild(errorIconPath);

    // Create text container
    const textContainer = document.createElement('div');
    textContainer.className = 'flex-1';

    // Create title
    const title = document.createElement('h3');
    title.className = 'text-sm font-semibold text-red-800 mb-1';
    title.textContent = 'Recording Failed to Start';

    // Create message
    const messageEl = document.createElement('p');
    messageEl.className = 'text-sm text-red-700';
    messageEl.textContent = message;

    // Create right side (close button)
    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className =
      'ml-3 text-red-400 hover:text-red-600 transition-colors';
    closeButton.onclick = () => errorContainer.remove();

    // Create close icon
    const closeIcon = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'svg',
    );
    closeIcon.setAttribute('class', 'w-4 h-4');
    closeIcon.setAttribute('fill', 'currentColor');
    closeIcon.setAttribute('viewBox', '0 0 20 20');
    const closeIconPath = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'path',
    );
    closeIconPath.setAttribute('fill-rule', 'evenodd');
    closeIconPath.setAttribute(
      'd',
      'M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z',
    );
    closeIconPath.setAttribute('clip-rule', 'evenodd');
    closeIcon.appendChild(closeIconPath);

    // Assemble the DOM structure
    textContainer.appendChild(title);
    textContainer.appendChild(messageEl);
    leftSide.appendChild(errorIcon);
    leftSide.appendChild(textContainer);
    closeButton.appendChild(closeIcon);
    contentWrapper.appendChild(leftSide);
    contentWrapper.appendChild(closeButton);
    errorCard.appendChild(contentWrapper);
    errorContainer.appendChild(errorCard);

    // Add to document
    document.body.appendChild(errorContainer);
  }

  destroy() {
    // Clean up all streams and permissions first
    cleanup();

    // Clean up any active title notifications
    cleanupTitleNotifications();

    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }

  async initialize() {
    const store = initStore([getMiddleware(this.eventEmitter)]);
    if (!this.parentElementSelector) {
      document.body.insertAdjacentHTML(
        'beforeend',
        `<div id="${this.parentElementSelector.replace('#', '')}"></div>`,
      );
    }
    const parentElement = document.querySelector(this.parentElementSelector);
    if (!parentElement) {
      throw new Error('Parent element not found');
    }

    // Retrieve settings from localStorage
    const localStorageSettings = localStorage.getItem('recorderSettings');
    const parsedSettings = localStorageSettings
      ? JSON.parse(localStorageSettings)
      : {};
    let audioSettings = {};
    let videoSettings = {};
    if (!this.isPlugin) {
      // Don't fetch settings again in plugin mode
      const {
        audioSettings: audioSettingsFromServer,
        videoSettings: videoSettingsFromServer,
      } = await getSettings('recorder');
      audioSettings = audioSettingsFromServer;
      videoSettings = videoSettingsFromServer;
    } else {
      // XXX: Set token in memory but don't store it in localStorage
      console.log('Setting token in memory:', this.apiKey);
      setToken(this.apiKey, false);
      setHeader(this.apiKey);
    }
    this.settings = {
      audioSettings: {
        ...audioSettings,
        ...this.settings?.audioSettings,
        ...parsedSettings?.audioSettings, // Merge with localStorage settings
      },
      videoSettings: {
        ...videoSettings,
        ...this.settings?.videoSettings,
        ...parsedSettings?.videoSettings, // Merge with localStorage settings
      },
    };
    await store.dispatch(
      setupState({
        parentElementSelector: this.parentElementSelector,
        uploadState: { teamId: this.teamId, folderId: this.folderId },
        settings: this.settings,
      }),
    );

    // Set the UI mode (fullscreen or popup)
    store.dispatch(setUIMode(this.uiMode));

    // Set that this recorder is mounted from the plugin only if it's actually from the plugin
    store.dispatch(setMountedFromPlugin(this.isPlugin));

    const RecorderComponent = getRecorderComponent(store);
    this.root = createRoot(parentElement);
    this.root.render(<RecorderComponent />);
    this.eventEmitter.emit('ready', {
      teamId: this.teamId,
      folderId: this.folderId,
    });
  }

  on<K extends keyof EventMap>(type: K, listener: EventListener<K>) {
    this.eventEmitter.on(type, listener);
  }

  off<K extends keyof EventMap>(type: K, listener: EventListener<K>) {
    this.eventEmitter.off(type, listener);
  }
}
